(() => {
    const DESIGN_WIDTH = 1920;
    const DESIGN_HEIGHT = 1200;
    const SCROLL_PAUSE = 1000;
    const VIEW_SWITCH_PAUSE = 5000;  // 切换等待时间
    const ACTION_COMPLETE_PAUSE = 2000; // 这里是滚动之后等待时间
    const MIN_SCROLL_DURATION = 1800;
    const SCROLL_SPEED = 36;

    let rankingScrollController = null;
    let rankingResizeObserver = null;
    let resizeTimer = null;
    let activeView = 'progress';
    let lastViewSwitchAt = 0;

    const getActiveViewElement = () => document.querySelector(`.grid-container[data-screen-view="${activeView}"]`);

    const updateHeaderTabs = () => {
        const tabs = document.querySelectorAll('.header-list p');

        tabs.forEach((tab, index) => {
            tab.classList.toggle('active', activeView === (index === 0 ? 'progress' : 'integral'));
        });
    };

    const switchView = (nextView) => {
        activeView = nextView;
        lastViewSwitchAt = Date.now();

        document.querySelectorAll('.grid-container[data-screen-view]').forEach((view) => {
            view.classList.toggle('is-active', view.dataset.screenView === activeView);
        });

        updateHeaderTabs();
        startRankingScroll(VIEW_SWITCH_PAUSE);
    };

    const switchToNextView = () => {
        switchView(activeView === 'progress' ? 'integral' : 'progress');
    };

    const getScaleRatio = () => {
        const screen = document.querySelector('.screen-active');
        const viewport = window.visualViewport;
        const rect = screen ? screen.getBoundingClientRect() : null;
        const viewportWidth = rect?.width || viewport?.width || document.documentElement.clientWidth || window.innerWidth;
        const viewportHeight = rect?.height || viewport?.height || document.documentElement.clientHeight || window.innerHeight;
        const widthRatio = viewportWidth / DESIGN_WIDTH;
        const heightRatio = viewportHeight / DESIGN_HEIGHT;

        return Math.min(widthRatio, heightRatio);
    };

    const updateScaleRatio = () => {
        const ratio = getScaleRatio();
        document.documentElement.style.setProperty('--scale-ratio', ratio.toFixed(4));

        return ratio;
    };

    const wait = (ms) => new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });

    const waitTransitionEnd = (element, duration) => new Promise((resolve) => {
        let timer = null;

        const cleanup = () => {
            window.clearTimeout(timer);
            element.removeEventListener('transitionend', onEnd);
            resolve();
        };

        const onEnd = (event) => {
            if (event.target === element) cleanup();
        };

        element.addEventListener('transitionend', onEnd);
        timer = window.setTimeout(cleanup, duration + 100);
    });

    const stopRankingScroll = () => {
        if (!rankingScrollController) return;

        rankingScrollController.abort();
        rankingScrollController = null;
    };

    const resetRankingBody = (body) => {
        body.style.transition = 'none';
        body.style.transform = 'translateY(0)';
    };

    const startRankingScroll = (initialPause = SCROLL_PAUSE) => {
        stopRankingScroll();

        const active = getActiveViewElement();
        const viewport = active?.querySelector('[data-ranking-viewport]');
        const body = active?.querySelector('[data-ranking-body]');
        if (!viewport || !body) return;

        resetRankingBody(body);

        const distance = Math.max(0, body.scrollHeight - viewport.clientHeight);
        if (distance <= 1) {
            rankingScrollController = new AbortController();
            const { signal } = rankingScrollController;

            wait(initialPause + ACTION_COMPLETE_PAUSE).then(() => {
                if (!signal.aborted) switchToNextView();
            });
            return;
        }

        rankingScrollController = new AbortController();
        const { signal } = rankingScrollController;
        const speed = SCROLL_SPEED * getScaleRatio();
        const duration = Math.max(MIN_SCROLL_DURATION, (distance / speed) * 1000);

        const run = async () => {
            while (!signal.aborted) {
                await wait(initialPause);
                if (signal.aborted) break;

                body.style.transition = `transform ${duration}ms linear`;
                body.style.transform = `translateY(-${distance}px)`;
                await waitTransitionEnd(body, duration);
                if (signal.aborted) break;

                await wait(SCROLL_PAUSE);
                if (signal.aborted) break;

                body.style.transition = `transform ${duration}ms linear`;
                body.style.transform = 'translateY(0)';
                await waitTransitionEnd(body, duration);
                if (signal.aborted) break;

                await wait(ACTION_COMPLETE_PAUSE);

                if (!signal.aborted) switchToNextView();
                break;
            }
        };

        run();
    };

    const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            updateScaleRatio();
            const initialPause = Date.now() - lastViewSwitchAt < VIEW_SWITCH_PAUSE ? VIEW_SWITCH_PAUSE : SCROLL_PAUSE;
            startRankingScroll(initialPause);
        }, 120);
    };

    const observeRankingResize = () => {
        if (!window.ResizeObserver || rankingResizeObserver) return;

        const rankingTargets = document.querySelectorAll('[data-ranking-viewport], [data-ranking-body], .grid-left, .integral-left');
        if (!rankingTargets.length) return;

        rankingResizeObserver = new ResizeObserver(handleResize);
        rankingTargets.forEach((target) => rankingResizeObserver.observe(target));
    };


    // 获取exl数据
    const fetchExcelRows = async (url) => {
        if (!url) {
            console.warn('Screen dashboard: window.fileData is missing.');
            return [];
        }

        if (!window.XLSX) {
            console.warn('Screen dashboard: XLSX library is not loaded.');
            return [];
        }

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to load Excel: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = window.XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rows = window.XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: ''
        });

        const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
        const dataRows = rows.slice(1).length ? rows.slice(1) : rows;
        const usedColumnIndexes = [];

        dataRows.forEach((row) => {
            row.forEach((cell, index) => {
                if (hasValue(cell) && !usedColumnIndexes.includes(index)) {
                    usedColumnIndexes.push(index);
                }
            });
        });

        return rows.map((row) => usedColumnIndexes.map((index) => row[index])).filter((row) => row.some(hasValue));
    };

    const initData = async () => {
        try {

            const dataRows = (await fetchExcelRows(window.fileData)).slice(1);

            if (typeof window.screenDashboardReader === 'function') {
                window.screenDashboardReader(dataRows);
            }
        } catch (error) {
            console.error('Screen dashboard: failed to load Excel data.', error);
        }
    };
    
    const renderRankingRows = (data) => {
        const hasProfit = document.querySelector('.screen-active')?.classList.contains('has-profit');
        const groupTrack = [
            {
                name: 'AMZ-NP',
                count: 10669279,
                amount:641178.59,
                number: 5,
                value: 101.19
            },
            {
                name: 'AMZ-ZLX',
                count: 2112617,
                amount: 92789.02,
                number: 4,
                value: 52.23
            },
            {
                name: 'DTC-NP',
                count: 340206,
                amount:14869.36,
                number: 4,
                value: 56.87
            },
            {
                name: 'TK-NP',
                count: 329200,
                amount: 6069.96,
                number: 4,
                value: 90.94
            }
        ];
        const toFiniteNumber = (value) => {
            const match = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
            const number = match ? Number(match[0]) : 0;
            return Number.isFinite(number) ? number : 0;
        };
        const clampProgress = (value) => Math.max(0, Math.min(100, value));
        const formatPercent = (value) => {
            const number = Number(value);
            if (!Number.isFinite(number)) return '0';

            return Number.isInteger(number) ? String(number) : number.toFixed(1);
        };
        const createCell = (className, text = '') => {
            const cell = document.createElement('span');
            cell.className = className;
            cell.textContent = text;

            return cell;
        };
        const createDistanceText = (percent, className = 'ranking-distance') => {
            const distance = document.createElement('span');
            const value = document.createElement('em');

            distance.className = className;
            distance.append(document.createTextNode(percent >= 100 ? '超 ' : '差 '));
            value.textContent = `${formatPercent(Math.abs(percent - 100))}%`;
            distance.append(value);

            return distance;
        };
        const createProgressCell = (percent, className = 'ranking-cell ranking-progress', showDistance = false) => {
            const cell = document.createElement('span');
            const track = document.createElement('span');
            const bar = document.createElement('i');
            const label = document.createElement('em');

            cell.className = className;
            track.className = 'ranking-progress__track';
            bar.style.setProperty('--progress-value', `${clampProgress(percent)}%`);
            label.textContent = `${formatPercent(percent)}%`;
            track.append(bar, label);
            cell.append(track);

            if (showDistance) {
                cell.append(createDistanceText(percent));
            }

            return cell;
        };

        const teamTemp = document.querySelector('#team-grid');
        if (teamTemp) {
            teamTemp.innerHTML = '';

            groupTrack.forEach((item) => {
                if (item.number === 0 && item.count === 0 && item.amount === 0) return;

                const card = document.createElement('div');
                const head = document.createElement('div');
                const title = document.createElement('p');
                const count = document.createElement('span');
                const progressLine = document.createElement('div');
                const progressMeta = document.createElement('div');
                const progressTitle = document.createElement('strong');
                const progressStatus = document.createElement('span');
                const progressStatusValue = document.createElement('em');
                const progressTrack = document.createElement('div');
                const progressBar = document.createElement('i');
                const gmvRate = item.count ? (item.amount / item.count) * 100 : 0;

                card.className = 'team-card';
                head.className = 'team-card__head';
                title.textContent = item.name;
                count.textContent = `${item.number}人`;
                head.append(title, count);

                progressLine.className = 'progress-line';
                progressMeta.className = 'progress-line__meta';
                progressTitle.textContent = `GMV ${formatPercent(gmvRate)}%`;
                progressStatus.append(document.createTextNode(gmvRate >= 100 ? '超 ' : '差 '));
                progressStatusValue.textContent = `${formatPercent(Math.abs(gmvRate - 100))}%`;
                progressStatus.append(progressStatusValue);
                progressMeta.append(progressTitle, progressStatus);
                progressTrack.className = 'progress-track';
                progressBar.style.setProperty('--progress-value', `${clampProgress(gmvRate)}%`);
                progressBar.style.setProperty('--progress-color', '#3AA7F3');
                progressTrack.append(progressBar);
                progressLine.append(progressMeta, progressTrack);
                card.append(head, progressLine);

                if (hasProfit && item.value) {
                    const profitLine = document.createElement('div');
                    const profitMeta = document.createElement('div');
                    const profitTitle = document.createElement('strong');
                    const profitStatus = document.createElement('span');
                    const profitStatusValue = document.createElement('em');
                    const profitTrack = document.createElement('div');
                    const profitBar = document.createElement('i');

                    profitLine.className = 'progress-line profit-line';
                    profitMeta.className = 'progress-line__meta';
                    profitTitle.textContent = `利润率 ${formatPercent(item.value)}%`;
                    profitStatus.append(document.createTextNode(item.value >= 100 ? '超 ' : '差 '));
                    profitStatusValue.textContent = `${formatPercent(Math.abs(item.value - 100))}%`;
                    profitStatus.append(profitStatusValue);
                    profitMeta.append(profitTitle, profitStatus);
                    profitTrack.className = 'progress-track profit-stack';
                    profitBar.style.setProperty('--progress-value', `${clampProgress(item.value)}%`);
                    profitBar.style.setProperty('--progress-color', '#BF55F2');
                    profitTrack.append(profitBar);
                    profitLine.append(profitMeta, profitTrack);
                    card.append(profitLine);
                }

                teamTemp.appendChild(card);
            });
        }

        const rankingPanel = document.getElementById('ranking-panel');
        if (!rankingPanel) {
            requestAnimationFrame(handleResize);
            return;
        }

        rankingPanel.querySelectorAll('.ranking-table, .ranking-table-profit').forEach((table) => table.remove());

        const rows = data.map((item) => {
            const targetGmv = toFiniteNumber(item[2]);
            const actualGmv = toFiniteNumber(item[3]);
            const profitRate = toFiniteNumber(item[4]);
            const gmvPercent = targetGmv ? (actualGmv / targetGmv) * 100 : 0;

            return {
                name: item[0],
                team: item[1],
                avatar: item[11],
                targetGmv,
                actualGmv,
                profitRate,
                gmvPercent
            };
        }).filter((item) => item.targetGmv > 0 || item.actualGmv > 0 || item.profitRate > 0)
            .sort((a, b) => b.gmvPercent - a.gmvPercent);

        const table = document.createElement('div');
        const header = document.createElement('div');
        const viewport = document.createElement('div');
        const body = document.createElement('div');
        const headers = hasProfit
            ? ['排名', '头像/姓名', 'GMV完成占比/差额', '6月利润完成占比/差额', '所属团队']
            : ['排名', '头像/姓名', 'GMV完成占比', '距离目标值', '所属团队'];

        table.className = hasProfit ? 'ranking-table ranking-table-profit' : 'ranking-table';
        header.className = 'ranking-table__head';
        headers.forEach((text) => header.appendChild(createCell('', text)));
        viewport.className = 'ranking-table__viewport';
        viewport.dataset.rankingViewport = '';
        body.className = 'ranking-table__body';
        body.dataset.rankingBody = '';

        rows.forEach((item, index) => {
            const rank = index + 1;
            const row = document.createElement('div');
            const rankCell = document.createElement('span');
            const rankIndex = document.createElement('i');
            const userCell = document.createElement('span');
            const name = document.createElement('em');

            row.className = 'ranking-row';
            rankCell.className = 'ranking-cell ranking-cell--rank';
            rankIndex.className = `ranking-index ranking-index--${rank}`;
            rankIndex.textContent = rank;
            rankCell.append(rankIndex);

            userCell.className = 'ranking-cell ranking-user';
            if (item.avatar) {
                const avatar = document.createElement('img');
                avatar.className = 'ranking-avatar';
                avatar.src = item.avatar;
                avatar.alt = '';
                avatar.loading = 'lazy';
                userCell.appendChild(avatar);
            } else {
                const avatar = document.createElement('i');
                avatar.className = 'ranking-avatar';
                avatar.textContent = String(item.name || '').slice(0, 1);
                userCell.appendChild(avatar);
            }
            name.textContent = item.name;
            userCell.appendChild(name);

            row.append(
                rankCell,
                userCell,
                createProgressCell(item.gmvPercent, 'ranking-cell ranking-progress', hasProfit),
                hasProfit
                    ? createProgressCell(item.profitRate, 'ranking-cell ranking-progress ranking-progress--profit', true)
                    : createDistanceText(item.gmvPercent, 'ranking-cell ranking-distance'),
                createCell('ranking-cell ranking-team', item.team)
            );
            body.appendChild(row);
        });

        viewport.appendChild(body);
        table.append(header, viewport);
        rankingPanel.appendChild(table);

        // 积分排行榜

        const integralBody = document.querySelector('.integral-table__body');
        if (integralBody) {
            integralBody.innerHTML = '';

            const integralRows = data.map((item) => ({
                name: item[0],
                team: item[1],
                avatar: item[11],
                spent: toFiniteNumber(item[5]),
                earned: toFiniteNumber(item[6]),
                trend: toFiniteNumber(item[10])
            })).filter((item) => item.name || item.team)
                .sort((a, b) => b.earned - a.earned);
            const integralTopList = document.querySelector('.integral-top-list');

            if (integralTopList) {
                integralTopList.innerHTML = '';

                integralRows.slice(0, 3).forEach((item, index) => {
                    const rank = index + 1;
                    const card = document.createElement('div');
                    const head = document.createElement('div');
                    const user = document.createElement('div');
                    const avatar = item.avatar ? document.createElement('img') : document.createElement('i');
                    const info = document.createElement('p');
                    const name = document.createElement('strong');
                    const team = document.createElement('span');
                    const badge = document.createElement('em');
                    const numberInfo = document.createElement('div');
                    const numberTitle = document.createElement('span');
                    const numberValue = document.createElement('em');
                    const stats = document.createElement('div');
                    const earnedItem = document.createElement('div');
                    const trendItem = document.createElement('div');
                    const spentItem = document.createElement('div');
                    const earnedValue = document.createElement('strong');
                    const trendValue = document.createElement('strong');
                    const spentValue = document.createElement('strong');
                    const firstEarned = integralRows[0]?.earned || item.earned;
                    const prevEarned = integralRows[index - 1]?.earned || firstEarned;

                    card.className = `integral-top-card integral-top-card--${rank}`;
                    head.className = 'integral-top-card__head';
                    user.className = 'integral-user';
                    avatar.className = 'ranking-avatar';
                    if (item.avatar) {
                        avatar.src = item.avatar;
                        avatar.alt = '';
                        avatar.loading = 'lazy';
                    } else {
                        avatar.textContent = String(item.name || '').slice(0, 1);
                    }
                    name.textContent = item.name;
                    team.textContent = item.team;
                    info.append(name, team);
                    badge.textContent = rank === 1 ? '冠军' : `NO.${rank}`;
                    user.append(avatar, info, badge);

                    numberInfo.className = 'number-info';
                    numberTitle.textContent = rank === 1 ? '领先积分榜' : `距离第${rank - 1}名差`;
                    numberValue.textContent = rank === 1 ? 'TOP 1' : `${formatPercent(prevEarned - item.earned)}积分`;
                    numberInfo.append(numberTitle, numberValue);
                    head.append(user, numberInfo);

                    stats.className = 'integral-stats';
                    earnedValue.textContent = formatPercent(item.earned);
                    earnedItem.append(createCell('', '累计获得积分'), earnedValue);

                    trendItem.append(createCell('', '趋势较前一日对比'));
                    trendValue.className = item.trend >= 0 ? 'is-up' : 'is-down';
                    trendValue.textContent = `${item.trend >= 0 ? '+' : ''}${formatPercent(item.trend)}`;
                    trendItem.appendChild(trendValue);

                    spentValue.textContent = formatPercent(item.spent);
                    spentItem.append(createCell('', '累计消耗积分'), spentValue);

                    stats.append(earnedItem, trendItem, spentItem);
                    card.append(head, stats);
                    integralTopList.appendChild(card);
                });
            }

            integralRows.slice(3).forEach((item, index) => {
                const rank = index + 4;
                const row = document.createElement('div');
                const rankCell = document.createElement('span');
                const rankIndex = document.createElement('i');
                const userCell = document.createElement('span');
                const avatar = item.avatar ? document.createElement('img') : document.createElement('i');
                const name = document.createElement('em');
                const trendCell = document.createElement('span');

                row.className = 'integral-row';
                rankIndex.className = 'ranking-index';
                rankIndex.textContent = rank;
                rankCell.appendChild(rankIndex);

                userCell.className = 'ranking-user';
                avatar.className = 'ranking-avatar';
                if (item.avatar) {
                    avatar.src = item.avatar;
                    avatar.alt = '';
                    avatar.loading = 'lazy';
                } else {
                    avatar.textContent = String(item.name || '').slice(0, 1);
                }
                name.textContent = item.name;
                userCell.append(avatar, name);

                trendCell.className = `integral-trend ${item.trend >= 0 ? 'is-up' : 'is-down'}`;
                trendCell.textContent = `${item.trend >= 0 ? '+' : ''}${formatPercent(item.trend)}`;

                row.append(
                    rankCell,
                    userCell,
                    createCell('integral-earned', formatPercent(item.earned)),
                    createCell('', formatPercent(item.spent)),
                    trendCell,
                    createCell('integral-team', item.team)
                );
                integralBody.appendChild(row);
            });
        }




        if (rankingResizeObserver) {
            rankingResizeObserver.observe(viewport);
            rankingResizeObserver.observe(body);
        }

        requestAnimationFrame(handleResize);
    };

    const init = async () => {
        activeView = document.querySelector('.grid-container.is-active')?.dataset.screenView || 'progress';
        window.screenDashboardReader = renderRankingRows;
        
        updateScaleRatio();
        updateHeaderTabs();
        startRankingScroll();
        observeRankingResize();
        requestAnimationFrame(handleResize);
        window.addEventListener('resize', handleResize);
        window.addEventListener('load', handleResize);
        window.addEventListener('orientationchange', handleResize);
        document.addEventListener('fullscreenchange', handleResize);
        document.addEventListener('webkitfullscreenchange', handleResize);
        window.visualViewport?.addEventListener('resize', handleResize);
        await initData();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
